import { ArrowDown, ArrowUp } from "lucide-react";
import type * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { SortState } from "@/components/shared/use-table-sort";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Opt-in multi-select wiring. When provided, the table renders a leading
 * "select all on this page" header checkbox column; the caller is responsible
 * for rendering the matching leading `<td>` checkbox cell in each row (see
 * `PersonsTable`). Selection state is fully controlled by the caller.
 */
interface DataTableSelection {
  /** Row ids currently rendered on this page — drives the select-all state. */
  pageIds: string[];
  selectedIds: Set<string>;
  /** true → select every row on the page, false → deselect them. */
  onToggleAll: (checked: boolean) => void;
}

export interface DataTableHeader {
  label: string;
  className?: string;
  /** When set (with `onSort`), this column header becomes a sort toggle. */
  sortKey?: string;
}

interface DataTableProps {
  headers: DataTableHeader[];
  children: React.ReactNode;
  loading?: boolean;
  empty?: React.ReactNode;
  colSpan?: number;
  className?: string;
  /** Active sort, used to render the direction arrow on the matching header. */
  sort?: SortState;
  /** Called with a header's `sortKey` when a sortable header is clicked. */
  onSort?: (key: string) => void;
  /** Opt-in row selection. When set, a leading checkbox column is rendered. */
  selection?: DataTableSelection;
  /**
   * Stable id under which the user's column widths persist in localStorage.
   * Defaults to the column labels, which is unique enough per table. Set it
   * explicitly if two tables share the same labels but want separate widths.
   */
  storageKey?: string;
}

/** Smallest a column can be dragged to. */
const MIN_COL_WIDTH = 60;
/** Auto-measured defaults are clamped so one long value can't blow out a column. */
const MAX_MEASURED_WIDTH = 360;

type Widths = Record<string, number>;

function loadWidths(key: string): Widths {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: Widths = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function saveWidths(key: string, widths: Widths) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(widths));
  } catch {
    // Best-effort: a full / disabled localStorage just means widths don't persist.
  }
}

export function DataTable({
  headers,
  children,
  loading = false,
  empty,
  colSpan,
  className,
  sort,
  onSort,
  selection,
  storageKey: storageKeyProp,
}: DataTableProps) {
  // The leading selection column adds one to the empty-state / skeleton spans.
  const span = (colSpan ?? headers.length) + (selection ? 1 : 0);

  // Select-all reflects only the rows visible on this page: fully checked when
  // every page row is selected, indeterminate when some are.
  const pageIds = selection?.pageIds ?? [];
  const selectedOnPage = pageIds.filter((id) => selection?.selectedIds.has(id)).length;
  const selectAllState: boolean | "indeterminate" =
    pageIds.length > 0 && selectedOnPage === pageIds.length
      ? true
      : selectedOnPage > 0
        ? "indeterminate"
        : false;
  const hasChildren = Array.isArray(children)
    ? children.filter(Boolean).length > 0
    : Boolean(children);
  const showEmpty = !loading && !hasChildren && empty;

  const headerSig = headers.map((h) => h.label).join("|");
  const storageKey = `dt-cols:${storageKeyProp ?? headerSig}`;

  const [widths, setWidths] = useState<Widths>(() => loadWidths(storageKey));
  // When true the table renders in measure mode (auto layout, content-sized) for
  // one commit so we can capture each visible column's natural unwrapped width.
  const [measuring, setMeasuring] = useState(false);

  const thRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  // Refs mirror the latest values so the effects/handlers don't need them as deps.
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  const headersRef = useRef(headers);
  headersRef.current = headers;

  // Trigger a measure pass once real rows are present and a visible column has no
  // width yet (initial load, or a column revealed at a larger breakpoint).
  useLayoutEffect(() => {
    if (loading || measuring) return;
    const need = headersRef.current.some((h, i) => {
      const th = thRefs.current[i];
      return Boolean(th) && th?.offsetParent != null && widthsRef.current[h.label] == null;
    });
    if (need) setMeasuring(true);
  }, [loading, measuring]);

  // Capture natural widths while in measure mode, then return to fixed layout.
  useLayoutEffect(() => {
    if (!measuring) return;
    const next: Widths = { ...widthsRef.current };
    let changed = false;
    headersRef.current.forEach((h, i) => {
      const th = thRefs.current[i];
      if (th && th.offsetParent != null && next[h.label] == null) {
        const w = Math.round(th.getBoundingClientRect().width);
        next[h.label] = Math.max(MIN_COL_WIDTH, Math.min(MAX_MEASURED_WIDTH, w));
        changed = true;
      }
    });
    setMeasuring(false);
    if (changed) {
      setWidths(next);
      saveWidths(storageKey, next);
    }
  }, [measuring, storageKey]);

  // A wider viewport can reveal a previously-hidden column that still needs a width.
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const need = headersRef.current.some((h, i) => {
          const th = thRefs.current[i];
          return Boolean(th) && th?.offsetParent != null && widthsRef.current[h.label] == null;
        });
        if (need) setMeasuring(true);
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const drag = useRef<{ key: string; startX: number; startW: number } | null>(null);

  // Drag tracking lives on window, not on the handle element, so a re-render that
  // swaps the <th> (filters, react-query refetches) can't drop the gesture midway.
  useEffect(() => {
    function onMove(e: PointerEvent) {
      const d = drag.current;
      if (!d) return;
      const w = Math.max(MIN_COL_WIDTH, Math.round(d.startW + (e.clientX - d.startX)));
      setWidths((prev) => (prev[d.key] === w ? prev : { ...prev, [d.key]: w }));
    }
    function onUp() {
      if (!drag.current) return;
      drag.current = null;
      document.body.style.userSelect = "";
      saveWidths(storageKey, widthsRef.current);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [storageKey]);

  function onHandleDown(e: React.PointerEvent, key: string, startW: number) {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { key, startX: e.clientX, startW };
    // Suppress text selection across the table while dragging.
    document.body.style.userSelect = "none";
  }

  return (
    <div className={cn("overflow-x-auto rounded-lg border", className)}>
      <table
        className={cn("text-sm", measuring && "[&_td]:whitespace-nowrap")}
        style={
          measuring
            ? { tableLayout: "auto", width: "max-content" }
            : // `width: max-content` makes the table grow to the sum of its column
              // widths (so columns keep the size you set and the wrapper scrolls);
              // `minWidth: 100%` still fills the container when there's room.
              { tableLayout: "fixed", width: "max-content", minWidth: "100%" }
        }
      >
        <thead>
          <tr className="border-b bg-muted/50">
            {selection && (
              <th className="w-10 px-3 py-3 text-left" style={{ width: 40 }}>
                <Checkbox
                  aria-label="Select all on this page"
                  checked={selectAllState}
                  onCheckedChange={(checked) => selection.onToggleAll(checked === true)}
                  disabled={pageIds.length === 0}
                />
              </th>
            )}
            {headers.map((header, index) => {
              const sortKey = header.sortKey;
              const sortable = sortKey != null && onSort != null;
              const active = sortable && sort?.key === sortKey;
              const width = widths[header.label];
              return (
                <th
                  key={header.label}
                  ref={(el) => {
                    thRefs.current[index] = el;
                  }}
                  style={!measuring && width != null ? { width } : undefined}
                  className={cn(
                    "relative whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-muted-foreground",
                    header.className,
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(sortKey)}
                      className="inline-flex select-none items-center gap-1 transition-colors hover:text-foreground"
                    >
                      {header.label}
                      {active &&
                        (sort?.dir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        ))}
                    </button>
                  ) : (
                    header.label
                  )}
                  <span
                    aria-hidden="true"
                    title="Drag to resize"
                    onPointerDown={(e) =>
                      onHandleDown(
                        e,
                        header.label,
                        width ?? thRefs.current[index]?.getBoundingClientRect().width ?? 120,
                      )
                    }
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-border"
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className="border-b last:border-b-0">
                {selection && (
                  <td key={`skeleton-${rowIndex}-select`} className="px-3 py-2.5">
                    <Skeleton className="size-4" />
                  </td>
                )}
                {headers.map((_header, colIndex) => (
                  <td key={`skeleton-${rowIndex}-${colIndex}`} className="px-4 py-2.5">
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : showEmpty ? (
            <tr>
              <td colSpan={span}>{empty}</td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}
